/** Compact conversation transcript for chat Agent Review cases. */

import clsx from "clsx";
import type { ChatMessageEvidence } from "./types";
import { ChatEmbedEvidence } from "./ChatEmbedEvidence";
import { plainChatText } from "./plainChatText";

interface ChatThreadEvidenceProps {
  messages: ChatMessageEvidence[];
  agentName?: string;
  className?: string;
}

export function ChatThreadEvidence({
  messages,
  agentName = "Worker",
  className,
}: ChatThreadEvidenceProps) {
  if (!messages.length) {
    return (
      <p className={clsx("text-sm text-slate-500", className)}>No messages in this conversation.</p>
    );
  }

  return (
    <div className={clsx("space-y-3", className)} data-testid="agent-review-chat-thread">
      {messages.map((message) => {
        const isUser = message.sender_type === "user";
        const isAgent = message.sender_type === "agent";
        const label = isUser ? "User" : isAgent ? agentName : "System";
        const text = plainChatText(message.text);
        const embeds = message.embeds ?? [];
        if (!text && embeds.length === 0) return null;
        return (
          <div
            key={message.message_id}
            className={clsx(
              "rounded-xl px-3 py-2.5 ring-1",
              isUser && "bg-indigo-500/10 ring-indigo-400/20",
              isAgent && "bg-white/[0.03] ring-white/10",
              !isUser && !isAgent && "bg-slate-800/40 ring-white/5",
            )}
          >
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">
              {label}
            </p>
            {text ? (
              <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{text}</p>
            ) : null}
            <ChatEmbedEvidence embeds={embeds} />
          </div>
        );
      })}
    </div>
  );
}
